import * as $ from "jquery";
import Breakpoint from "./per-user-pricing-calculator-breakpoint";
import imkt from '../../imkt';
import general from '../../utils/general';

const DEFAULT_TIER_MODEL = "pricingTiersMonthly";

export default class PerUserPricingCalculator {
  domRoot: JQuery<HTMLElement>;
  params: any;
  userInput: JQuery<HTMLElement>;
  calculateButton: JQuery<HTMLElement>;
  priceLabel: JQuery<HTMLElement>;
  priceUnit: JQuery<HTMLElement>;
  averagePriceLabel: JQuery<HTMLElement>;
  outputHeading: any;
  breakdown: any;
  breakdownContainer: any;
  activeTierModelName: any;
  activeTierModel: any;
  
  constructor(domRoot: HTMLElement, params: any) {
    this.domRoot = $(domRoot);
    this.params = {
      // additional default params
      currencyLabel: '<span class="priceCurrency">$</span>',
      ...params,
    };

    // Component HTML elements
    this.userInput = $(".imkt-pup-calculator__user-input", this.domRoot);
    this.calculateButton = $(".imkt-pup-calculator__calculate", this.domRoot);
    this.priceLabel = $(".imkt-pup-calculator__price", this.domRoot);
    this.priceUnit = $(".imkt-pup-calculator__price-label", this.domRoot);
    this.averagePriceLabel = $(".imkt-pup-calculator__average", this.domRoot);
    this.outputHeading = this.domRoot.find(".imkt-pup-calculator__sub-heading");
    this.breakdown = this.domRoot.find(".imkt-pup-calculator__price-breakdown");
    this.breakdownContainer = this.breakdown.find(
      ".imkt-pup-calculator__price-breakdown__container"
    );

    this.init();

    if (this.userInput.val().trim() !== "") {
      this.calculateButton.trigger("click");
    }
  }

  init() {
    if (
      !this.params.hasOwnProperty("useTiers") ||
      !$.isEmptyObject(this.params.useTiers)
    ) {
      this.params.useTiers = [DEFAULT_TIER_MODEL];
    }

    this.setFormattedMaxUserTierError();

    this.setActiveTierModel(this.params.useTiers[0]);

    this.setMaxTierPrice();
    this.bindEvents();
  }

  bindEvents() {
    this.calculateButton.on(
      "click",
      function () {
        this.validateUserInput();

        if (this.userInput.val().trim() === "") {
          this.resetPrice();
          this.userInput.focus();
        }

        return false;
      }.bind(this)
    );

    this.userInput.on(
      "blur",
      function () {
        if (this.userInput.val().trim() === "") {
          this.resetErrors();
          this.resetPrice();
        } else {
          this.validateUserInput();
        }
      }.bind(this)
    );

    this.userInput.on("keydown", function (e) {
      // Key is not a number.
      const isNotNumericKey = /[\d]+/.test(e.key) === false;

      // "Shift", "Alt", "Ctrl", etc.; Allow these characters.
      const isHelperKey = /[a-zA-Z]{2,}/.test(e.key);
      const isInvalidKey = isNotNumericKey !== isHelperKey;

      if (isInvalidKey) {
        e.preventDefault();
      }
    });
    this.domRoot.on("change", 'input[name="tierModel"]', (e) => {
      const selectedTierModel = e.currentTarget.value;
      this.setActiveTierModel(selectedTierModel);

      if (this.userInput.val().trim() === "") {
        this.resetErrors();
        this.resetPrice();
      } else {
        this.validateUserInput();
      }
    });
  }

  validateUserInput() {
    var userCount = this.userInput.val();
    var numberRegExp = /^[1-9]\d*$/;
    var isNumber = numberRegExp.test(userCount);

    if (isNumber && parseInt(userCount) !== 0) {
      this.priceLookup(parseInt(userCount));
    } else {
      this.errorNaN();
    }
  }

  setActiveTierModel(tierModelName) {
    this.activeTierModelName = tierModelName;
    this.activeTierModel = this.params.tierModels[this.activeTierModelName];

    const priceLabel = this.activeTierModel.priceLabel;

    this.priceUnit.text(priceLabel);
    this.domRoot.attr("data-activetier", this.activeTierModelName);
  }

  setMaxTierPrice() {
    $.each(
      this.activeTierModel.tiers,
      function (index, tier) {
        var userRangeCount = tier.maxUserCount + 1 - tier.minUserCount;
        tier.maxTierPrice =
          tier.maxUserCount === -1 ? null : userRangeCount * tier.perUserPrice;
      }.bind(this)
    );
  }

  priceLookup(userCount) {
    var price = 0;
    var userCountInRange = 0;

    // Sort tiers by minUserCount
    this.activeTierModel.tiers.sort((a, b) => {
      return a.minUserCount - b.minUserCount;
    });

    if (this.activeTierModel.pricingStrategy === "fixedTiers") {
      this.isNotFlatPrice();

      $.each(
        this.activeTierModel.tiers,
        function (index, tier) {
          if (
            userCount >= tier.minUserCount &&
            (userCount <= tier.maxUserCount || tier.maxUserCount === -1)
          ) {
            switch (tier.priceModel) {
              case "flat":
                price = tier.flatPrice;
                break;

              case "perUser":
                price = userCount * tier.perUserPrice;
                break;

              case "perUserGroup":
                // A + (B * Math.floor((C - D) / E)
                price =
                  tier.perUserGroupStartingPrice +
                  tier.perUserGroupPriceIncrease *
                    Math.floor(
                      (userCount - tier.minUserCount) / tier.perUserGroupCount
                    );
                break;
            }
            return false;
          }
        }.bind(this)
      );
    } else {
      $.each(
        this.activeTierModel.tiers,
        function (index, tier) {
          // if price is flat
          if (tier.priceModel === "flat") {
            // if usercount is within flat rate tier, use flatPrice, else use maxTierPrice which is based on per user price
            if (userCount <= tier.maxUserCount) {
              price = tier.flatPrice;
              this.isFlatPrice();
            } else {
              price = price + tier.maxTierPrice;
              this.isNotFlatPrice();
            }
            // else get lower tier max prices excluding max tier and excluding fixed
          } else if (
            userCount > tier.maxUserCount &&
            tier.maxUserCount !== -1
          ) {
            price = price + tier.maxTierPrice;
            // then if not at max tier, get current/active tier
          } else if (
            userCount >= tier.minUserCount &&
            userCount <= tier.maxUserCount
          ) {
            userCountInRange = userCount + 1 - tier.minUserCount;
            price = price + userCountInRange * tier.perUserPrice;
            // if price is max tier (maxUserCount = -1; used to denote infinity)
          } else if (
            userCount >= tier.minUserCount &&
            tier.maxUserCount === -1
          ) {
            userCountInRange = userCount + 1 - tier.minUserCount;
            price = price + userCountInRange * tier.perUserPrice;
          }
        }.bind(this)
      );
    }

    const averagePrice = price / userCount;

    const calculationDetails = this.getCalculationDetails(
      userCount,
      this.activeTierModel.tiers
    );

    this.updatePrice(
      price.toFixed(2).replace(".00", ""),
      averagePrice.toFixed(2),
      userCount,
      calculationDetails
    );
    this.overMaxUsers(userCount);
    this.overMinUsers(userCount);
    this.dismissErrorNaN();
  }

  // @todo At some point replace parts of "priceLookup" with this
  getCalculationDetails(userCount, tiers) {
    const breakdown = [];
    let total = 0;
    let userCountdown = userCount;

    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      t.tierSize =
        t.maxUserCount > 0
          ? t.maxUserCount - t.minUserCount + 1
          : userCount - t.minUserCount + 1;

      if (t.priceModel === "flat") {
        // Number of users fall outside the "flat pricing" range
        // Treat as "per-user"
        if (userCountdown > t.maxUserCount) {
          userCountdown -= t.maxUserCount;
          breakdown.push(
            new Breakpoint(
              "flat-perUser-go",
              t.perUserPrice,
              t.maxUserCount,
              true
            )
          );
          total += t.perUserPrice * t.maxUserCount;

          // Qualifies for flat rate, regardless of number of users
        } else {
          breakdown.push(
            new Breakpoint(
              "flat-stop",
              t.flatPrice,
              t.maxUserCount,
              true,
              this.params.breakdownFlatPriceTemplate
            )
          );
          total += t.flatPrice;
          break;
        }
      } else if (t.priceModel === "perUser") {
        // Remaining number of users is bigger than the current tier
        // Use up entire tier; keep going
        if (userCountdown > t.tierSize) {
          userCountdown -= t.tierSize;
          breakdown.push(
            new Breakpoint("perUser-go", t.perUserPrice, t.tierSize)
          );
          total += t.perUserPrice * t.tierSize;
        }

        // Use as much of this tier as you need; stop
        else {
          breakdown.push(
            new Breakpoint("perUser-stop", t.perUserPrice, userCountdown)
          );
          total += t.perUserPrice * userCountdown;
          userCountdown = 0;
          break;
        }
      }
    }

    const avg = Math.round((total / userCount) * 100) / 100;

    return {
      userCount,
      total,
      avg,
      breakdown,
    };
  }

  getTier(userCount) {
    const tier = this.activeTierModel.tiers.find(
      (tier) =>
        tier.minUserCount &&
        (userCount <= tier.maxUserCount || tier.maxUserCount === -1)
    );
    return (
      tier.minUserCount +
      (tier.maxUserCount === -1 ? "+" : ` - ${tier.maxUserCount}`) +
      ` ${this.params.usersString}`
    );
  }

  updatePrice(price, averagePrice, userCount, details) {
    // Sort by price (low to high)
    const breakdown = [...details.breakdown].sort(
      (a, b) => parseInt(b.price) - parseInt(a.price)
    );
    let breakdownHtml = [];

    if (breakdown.length > 1) {
      const firstBreakpoint = breakdown.shift();
      const secondBreakpoint = breakdown.shift();

      if (
        firstBreakpoint.isFlatPrice &&
        firstBreakpoint.price === secondBreakpoint.price
      ) {
        const label = `${firstBreakpoint.label} + ${secondBreakpoint.label}`;
        const userCount =
          firstBreakpoint.userCount + secondBreakpoint.userCount;
        const comboBreakpoint = new Breakpoint(
          label,
          firstBreakpoint.price,
          userCount,
          true
        );

        breakdown.unshift(comboBreakpoint);
      } else {
        breakdown.unshift(firstBreakpoint, secondBreakpoint);
      }
    }

    this.breakdownContainer.html("");
    breakdown.forEach((breakpoint) => {
      breakdownHtml.push(`<span>${breakpoint}</span>`);
    });

    const i18nPrice = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    breakdownHtml = [breakdownHtml.join(" + ")];

    if (breakdown.length > 1) {
      breakdownHtml.push(` = <strong>${i18nPrice}</strong>`);
    }

    this.breakdownContainer.html($(breakdownHtml.join("")));
    this.breakdown.show();

    this.priceLabel.html(
      this.params.currencyLabel + general.numberWithCommas(price)
    );
    this.averagePriceLabel.html(this.params.currencyLabel + averagePrice);

    if (this.params.showUserRange) {
      const tierUserRange = ` (${this.getTier(userCount)})`;
      if (this.outputHeading.find("small").length) {
        this.outputHeading.find("small").text(tierUserRange);
      } else {
        this.outputHeading.append($(`<small>${tierUserRange}</small>`));
      }
    }
  }

  resetErrors() {
    $(this.domRoot).removeClass("has-input-error is-max-user is-min-user");
  }

  resetPrice() {
    this.priceLabel.text(this.params.priceLabelValue);
    this.averagePriceLabel.text(this.params.priceLabelValue);
    this.outputHeading.find("small").remove();
    this.breakdown.hide();
  }

  isFlatPrice() {
    $(this.domRoot).addClass("is-flat-price");
  }

  isNotFlatPrice() {
    $(this.domRoot).removeClass("is-flat-price");
  }

  overMaxUsers(userCount) {
    if (this.params.maxUserInt > 0 && userCount > this.params.maxUserInt) {
      this.resetPrice();
      $(this.domRoot).addClass("is-max-user");
    } else {
      $(this.domRoot).removeClass("is-max-user");
    }
  }

  overMinUsers(userCount) {
    if (userCount < this.params.minUserInt) {
      this.resetPrice();
      $(this.domRoot).addClass("is-min-user");
    } else {
      $(this.domRoot).removeClass("is-min-user");
    }
  }

  errorNaN() {
    this.resetPrice();
    $(this.domRoot).addClass("has-input-error");
  }

  dismissErrorNaN() {
    $(this.domRoot).removeClass("has-input-error");
  }

  setFormattedMaxUserTierError() {
    const errorMessageContainer = $(".imkt-pup-calculator__error-max-user");
    const errorMessageMessage = errorMessageContainer.html();
    errorMessageContainer.html(
      errorMessageMessage.replace(
        /[.,\d]+/,
        this.formatMaxUsers(this.params.maxUserInt)
      )
    );
  }

  formatMaxUsers(users) {
    if (
      general &&
      typeof general.numberWithCommas === "function"
    ) {
      return general.numberWithCommas(users);
    }
    return users;
  }
}
